import { ORDER_STATUS_FILTERS } from "@bcc/components/orders/constants";
import type { PurchaseOrder, PurchaseOrderStatus } from "@shared/types";

export interface DemoOrdersOptions {
  /** Carrier account the rows belong to; taken from the roaster so row rendering finds a carrier */
  customerAccountNumber: string;
  /** Roaster's country, so addresses look local */
  countryCode: string;
  /** Anchor for the relative order dates; injectable for tests */
  now?: Date;
}

interface DemoOrderSeed {
  status: PurchaseOrderStatus;
  hoursAgo: number;
  customerName: string;
  city: string;
  productTitle: string;
  variantTitle: string;
  quantity: string;
  weightInGrams: number;
  total: string;
}

const HOUR_MS = 3_600_000;

// Mostly waiting orders (the tour's batch and summary steps need them), plus one of each later stage
const DEMO_ORDER_SEEDS: readonly DemoOrderSeed[] = [
  {
    status: "WaitingToBeFulfilled",
    hoursAgo: 2,
    customerName: "Alex Morgan",
    city: "Springfield",
    productTitle: "Single Origin Ethiopia",
    variantTitle: "340g Whole Bean",
    quantity: "1",
    weightInGrams: 340,
    total: "21.00",
  },
  {
    status: "WaitingToBeFulfilled",
    hoursAgo: 5,
    customerName: "Sam Rivera",
    city: "Riverton",
    productTitle: "House Blend",
    variantTitle: "1kg Espresso",
    quantity: "2",
    weightInGrams: 2000,
    total: "64.00",
  },
  {
    status: "WaitingToBeFulfilled",
    hoursAgo: 9,
    customerName: "Jamie Lee",
    city: "Fairview",
    productTitle: "Decaf Colombia",
    variantTitle: "250g Filter",
    quantity: "1",
    weightInGrams: 250,
    total: "16.50",
  },
  {
    status: "WaitingToBeFulfilled",
    hoursAgo: 14,
    customerName: "Chris Taylor",
    city: "Georgetown",
    productTitle: "Single Origin Kenya",
    variantTitle: "340g Whole Bean",
    quantity: "3",
    weightInGrams: 1020,
    total: "63.00",
  },
  {
    status: "Fulfilled",
    hoursAgo: 30,
    customerName: "Jordan Kim",
    city: "Clinton",
    productTitle: "House Blend",
    variantTitle: "340g Espresso",
    quantity: "1",
    weightInGrams: 340,
    total: "19.00",
  },
  {
    status: "Shipped",
    hoursAgo: 52,
    customerName: "Taylor Brooks",
    city: "Salem",
    productTitle: "Single Origin Brazil",
    variantTitle: "1kg Whole Bean",
    quantity: "1",
    weightInGrams: 1000,
    total: "38.00",
  },
  {
    status: "Delivered",
    hoursAgo: 120,
    customerName: "Morgan Reed",
    city: "Franklin",
    productTitle: "Decaf Colombia",
    variantTitle: "340g Filter",
    quantity: "2",
    weightInGrams: 680,
    total: "36.00",
  },
  {
    status: "AddressValidationError",
    hoursAgo: 20,
    customerName: "Riley Quinn",
    city: "Madison",
    productTitle: "Single Origin Ethiopia",
    variantTitle: "340g Filter",
    quantity: "1",
    weightInGrams: 340,
    total: "21.00",
  },
];

/** Sample orders in the shape the orders table renders, belonging to the roaster's own carrier account */
export const buildDemoOrders = ({ customerAccountNumber, countryCode, now = new Date() }: DemoOrdersOptions): PurchaseOrder[] =>
  DEMO_ORDER_SEEDS.map((seed, index) => {
    const sequence = index + 1;
    const createdAt = new Date(now.getTime() - seed.hoursAgo * HOUR_MS).toISOString();
    return {
      vendorKey: "demo-vendor",
      purchaseOrderId: `DEMO-PO-${1000 + sequence}`,
      webOrderNumber: `DEMO-${1000 + sequence}`,
      salesOrderId: `DEMO-SO-${1000 + sequence}`,
      orderStatus: seed.status,
      vendorSystem: "shopify",
      customerAccountNumber,
      customerAccountName: customerAccountNumber,
      beanzConnectLabelPrintEnabled: true,
      createdAt,
      expectedFulfillmentDate: new Date(now.getTime() + 24 * HOUR_MS).toISOString(),
      shippingAddress: {
        name: seed.customerName,
        address1: `${10 + sequence} Coffee Lane`,
        city: seed.city,
        province: "",
        zip: `${10000 + sequence * 111}`,
        country: countryCode,
      },
      orderTotal: seed.total,
      orderWeightInGrams: seed.weightInGrams,
      lineItems: [
        {
          id: `DEMO-LI-${sequence}`,
          productTitle: seed.productTitle,
          variantTitle: seed.variantTitle,
          grindType: "Whole bean",
          quantity: seed.quantity,
          vendorSystem: "shopify",
          sourceProductSku: `DEMO-SKU-${sequence}`,
        },
      ],
    };
  });

/** Mirrors the server-side status filter: the URL param is a comma-separated status list */
export const filterDemoOrders = (orders: readonly PurchaseOrder[], orderStatusParam: string): PurchaseOrder[] => {
  const statuses = new Set(orderStatusParam.split(","));
  return orders.filter((order) => order.orderStatus !== undefined && statuses.has(order.orderStatus));
};

/** Per-chip order counts keyed like the real status-counts hook (`ORDER_STATUS_FILTERS` keys) */
export const buildDemoStatusCounts = (orders: readonly PurchaseOrder[]): Record<string, number> =>
  Object.fromEntries(
    ORDER_STATUS_FILTERS.map(({ key, statuses }) => [
      key,
      orders.filter((order) => order.orderStatus !== undefined && statuses.includes(order.orderStatus)).length,
    ])
  );
